import React, { useEffect, useState } from 'react'
import { RouteComponentProps } from '@reach/router'

import { Alert, Box, Button, Stack, Text, TextField } from '@myxplor/stardust'
import { Container, Header, Footer, Content } from '~/layouts/ScrollableContentLayout'
import KeyIcon from '~/icons/Key'
import { filterNonNumeric } from '~/utils'

const pinLength = 4

interface Props extends RouteComponentProps {
  errorMessage?: Nullable<string>
  onClear?(): void
  onNext(pin: string): void
  onBack(): void
}

const SetPin = ({ errorMessage, onClear, onNext, onBack }: Props) => {
  const [pin, setPin] = useState('')
  const [repeatPin, setRepeatPin] = useState('')
  const [displayError, setDisplayError] = useState(false)

  useEffect(() => {
    onClear && onClear()
  }, [])

  const validateLength = () => pin.length == pinLength

  const validateMatch = () => pin.length > 0 && pin === repeatPin

  const validatePin = () => validateMatch() && validateLength()

  return (
    <Container>
      <Header icon="back" onBack={onBack} data-qa="create-pin-title">
        Create Account Pin
      </Header>
      <Content>
        <Stack space="large">
          <Text color="higherEmphasis" prefab="body">
            Your four digit PIN can be used with your phone number to sign into Xplor.
          </Text>
          <Stack space="small">
            <TextField
              label="Pin"
              name="xplor_pin"
              type="password"
              pattern="[0-9]{4}"
              minLength={pinLength}
              maxLength={pinLength}
              value={pin}
              data-qa="xplor-pin-input"
              onChange={(value: string) => {
                setDisplayError(false)
                setPin(filterNonNumeric(value))
              }}
              trailingIcon={
                <Box height={24} width={24} color="primary">
                  <KeyIcon />
                </Box>
              }
            />
            <TextField
              label="Repeat Pin"
              name="repeat_xplor_pin"
              type="password"
              minLength={pinLength}
              maxLength={pinLength}
              value={repeatPin}
              data-qa="xplor-repeat-pin-input"
              onChange={(value: string) => {
                setDisplayError(false)
                setRepeatPin(filterNonNumeric(value))
              }}
              trailingIcon={
                <Box height={24} width={24} color="primary">
                  <KeyIcon />
                </Box>
              }
            />
            <Text decorate={validateLength() ? 'thru' : 'none'} prefab="caption">
              {pinLength} digits
            </Text>
            <Text decorate={validateMatch() ? 'thru' : 'none'} prefab="caption">
              pins don&apos;t match
            </Text>
            {displayError && errorMessage && <Alert tone="negative">{errorMessage}</Alert>}
          </Stack>
        </Stack>
      </Content>
      <Footer>
        {/* Box wrapper carries data-qa: stardust Button does not forward it to the DOM */}
        <Box data-qa="next-button" width={1}>
          <Button
            fill
            size="large"
            disabled={!validatePin()}
            onClick={() => {
              setDisplayError(true)
              onNext(pin)
            }}>
            Next
          </Button>
        </Box>
      </Footer>
    </Container>
  )
}

export default SetPin
